import Pusher from 'pusher-js'
import { onUnmounted, watch } from 'vue'
import { useBatchStore } from '@/stores/BatchStore'
import { useConnectionStore } from '@/stores/ConnectionStore'
import { useListingsStore } from '@/stores/ListingsStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useImportsStore } from '@/stores/ImportsStore'
import { useCardStore } from '@/stores/CardStore'
import { useRoute, useRouter } from 'vue-router'
import { PUSHER_EVENTS_TO_CHANNELS, DEFAULT_TOAST_NOTIFICATION } from '@/constants'
import { isMobile } from '@/helpers/general'
import { useInventoryStore } from '@/stores/InventoryStore'

export function usePusher(userStore) {
  const batchStore = useBatchStore()
  const connectionStore = useConnectionStore()
  const globalStore = useGlobalStore()
  const inventoryStore = useInventoryStore()
  const configStore = useConfigStore()
  const cardStore = useCardStore()
  const importsStore = useImportsStore()
  const listingsStore = useListingsStore()

  const route = useRoute()
  const router = useRouter()

  let pusher = null
  let channel = null

  const initializePusher = () => {
    Pusher.logToConsole = true

    pusher = new Pusher('f1107eef8ca40861ee35', {
      cluster: 'mt1'
    })

    channel = pusher.subscribe(`channel-${userStore.user?.id}`)

    channel.bind('batchUpdate', handleBatchUpdate)
    channel.bind('collx_listing_completed', (data, event) => handleImportCompleted(data, event, 'collx_listing_completed'))
    channel.bind('collx_import_completed', (data, event) => handleImportCompleted(data, event, 'collx_import_completed'))
    channel.bind('ebay_import_completed', (data, event) => handleImportCompleted(data, event, 'ebay_import_completed'))
    channel.bind('shopify_import_completed', (data, event) => handleImportCompleted(data, event, 'shopify_import_completed'))
    channel.bind('whatnot_import_completed', (data, event) => handleImportCompleted(data, event, 'whatnot_import_completed'))
    // channel.bind('ebay_import_failed', (data, event) => handleImportCompleted(data, event, 'ebay_import_failed'))
    channel.bind('ebay_import_failed', (data, event) => console.log('>>>--', data, event, 'ebay_import_failed'))
    channel.bind('otp_login_completed', handleOtpLoginCompleted)
    channel.bind('selected_image_side', handleSelectedImageSide)
    channel.bind('otp_login_ended', handleOtpLoginEnded)
    channel.bind('collx_auth_failed', handleCollxAuthFailed)

    channel.bind('ebay_listings_import_started', (data) => handleImportInventory('ebay', data))
    channel.bind('ebay_listings_import_completed', (data) => handleImportInventory('ebay', data))
    channel.bind('ebay_listings_import_progress', (data) => handleImportInventory('ebay', data))

    channel.bind('collx_listings_import_started', (data) => handleImportInventory('collx', data))
    channel.bind('collx_listings_import_completed', (data) => handleImportInventory('collx', data))
    channel.bind('collx_listings_import_progress', (data) => handleImportInventory('collx', data))

    channel.bind('whatnot_listings_import_started', (data) => handleImportInventory('whatnot', data))
    channel.bind('whatnot_listings_import_completed', (data) => handleImportInventory('whatnot', data))
    channel.bind('whatnot_listings_import_progress', (data) => handleImportInventory('whatnot', data))

    channel.bind('job_updated', (data) => handleJobProgress(data))

    channel.bind('subscription_cancelled', (data) => handleSubscriptionCancelled(data))

    channel.bind('listings_ended', (data) => handleListingsEnded(data))
  }

  const showListingsEndedToast = () => {
    listingsStore.additionalInfoAlreadyFetched = false
    listingsStore.fetchListingsAdditionalInfo()

    cardStore.selectedCardData = { ...cardStore.selectedCardData, listingStatus: 'ended' }

    globalStore.toastNotification = {
      show: true,
      message: `Your listing has ended.`,
      type: 'success',
    }

    globalStore.toastTimeoutId = setTimeout(() => {
      globalStore.toastNotification = { ...DEFAULT_TOAST_NOTIFICATION }
      globalStore.toastTimeoutId = null
    }, 5000)
  }

  // A bulk end (CDP-2459) already drives its own progress → completed toast from
  // the Manage-listings panel, and the user is looking at a grid rather than a
  // single listing. So refetch the rows so statuses / marketplace badges / the
  // per-item CTA reflect the post-action state, and leave the toast alone.
  // Inventory and Batch share PublishCardsMenu — refresh whichever grid is open
  // so Relist / End enablement update on both surfaces.
  const handleBulkListingsEnded = () => {
    listingsStore.bulkEndInFlight = false
    listingsStore.additionalInfoAlreadyFetched = false
    listingsStore.fetchListingsAdditionalInfo()

    const onBatchDetails = route.path.includes('/batches/') && batchStore.selectedBatchId

    if (onBatchDetails) {
      cardStore.fetchSelectedCards(cardStore.itemFilters || null, false)
      return
    }

    if (userStore.user?.default_inventory_id) {
      inventoryStore.fetchInventory({
        id: userStore.user.default_inventory_id,
        perPage: 25,
        isPagination: false,
      })
    }
  }

  const handleListingsEnded = (data) => {
    if (listingsStore.bulkEndInFlight) {
      handleBulkListingsEnded()
      return
    }

    if (globalStore.toastTimeoutId) {
      // First toast is still visible — let it finish its remaining time, then show second
      const elapsed = Date.now() - globalStore.toastStartTime
      const remaining = Math.max(0, 5000 - elapsed)

      clearTimeout(globalStore.toastTimeoutId)
      globalStore.toastTimeoutId = setTimeout(() => {
        globalStore.toastNotification = { ...DEFAULT_TOAST_NOTIFICATION }
        globalStore.toastStartTime = null
        showListingsEndedToast()
      }, remaining)
    } else {
      showListingsEndedToast()
    }
  }

  const handleJobProgress = (data) => {
    userStore.updateUserField('job_info', data.user?.job_info)

    console.log('job info updated -- job_updated', data)
  }

  const handleSubscriptionCancelled = (data) => {
    configStore.fetchConfigs()
  }

  /**
   * A finished import CREATES batches — the backend makes one per 500 items and
   * stamps `is_import` on them. The dealer watching the batches list sees
   * nothing happen, because that list is fetched on mount and driven by the
   * URL; the new batches only appear on a manual reload.
   *
   * So refetch it, using the query the page is ALREADY showing, which keeps the
   * dealer's filter, sort and page instead of snapping them back to defaults.
   * Deliberately not a `location.reload()`: that would also discard the import
   * dialog and anything else on screen to solve a stale list.
   *
   * Two conditions, and both matter:
   *   - only the LEGACY batches list. The new design fetches its list on entry
   *     and the dealer gets there by clicking Batches, so it is never stale.
   *   - only while that page is OPEN. Refetching a list nobody is looking at
   *     spends a request to populate a store the next visit would refill anyway.
   */
  const refreshBatchesAfterImport = () => {
    if (userStore.isNewDesign || route.name !== 'Batches') return

    batchStore.fetchBatches({
      page: route.query?.page || 1,
      status: route.query?.status || null,
      query: route.query?.sortBy,
    })
  }

  const handleImportInventory = (key, data) => {
    typeof userStore.user.import_progress === 'string' && (userStore.user.import_progress = JSON.parse(userStore.user.import_progress))
    userStore.user.import_progress[key] = data

    if ((!data.active || data.progress === 100) && !data.noItems) {
      inventoryStore.fetchInventory({ id: userStore.user.default_inventory_id, perPage: 25, isPagination: false })
      inventoryStore.fetchMarketPrices({ id: userStore.user.default_inventory_id, perPage: 25, isPagination: false })
      
      importsStore.showImportDialog = true
      importsStore.importDialogData.confirmLabel = `Import Completed`
      importsStore.importDialogData.confirmationMessage = data.totalItems ? `${data.totalItems} items have been added or updated in your inventory.` : 'Items have been added or updated in your inventory.' // TODO: need to fix it why we have noItems: false and totalItems: 0
      importsStore.importDialogData.singleOkButton = true

      refreshBatchesAfterImport()
    }

    if (data.noItems && !data.active) {
      importsStore.showImportDialog = true
      importsStore.importDialogData.confirmLabel = `Import Completed`
      importsStore.importDialogData.confirmationMessage = 'No items were available to import'
      importsStore.importDialogData.singleOkButton = true
    }

    // TODO: should be removed after fix event in backend
    setTimeout(() => {
      userStore.fetchUser()
    }, 2000)
  }

  const handleBatchUpdate = (data) => {
    if (
      data?.Event === 'card_processing_completed' ||
      data?.Event === 'card_processing_failed' ||
      data?.Event === 'rotate_processing_completed'
    ) {
      if (route.name === 'InventoryDetails') {
        inventoryStore.updatedInventoryCard(data.CardId, true)
        globalStore.pusherLoading = false
      } else {  
        cardStore.fetchUpdatedCard(data.CardId)
        console.log(`>>> ${data.Event} <<<`, data)

        // `fetchUpdatedCard` can only refresh a row that is ALREADY in the
        // list — it has no way to insert one. Cards created after the batch was
        // fetched (i.e. most of a large upload) therefore never appeared until
        // a manual refresh: a 66-card upload showed 19. On the last card of the
        // run, refetch the batch so the list matches what was processed.
        // Loader off: the rows are already on screen, and flashing a spinner
        // over a finished upload reads as another wait.
        if (data.LastCard) cardStore.fetchSelectedCards(null, false)
      }
    }

    if ( data?.Event === 'llm_search_started') {
      const cardStore = useCardStore()

      const card = cardStore.cards.find(item => item.id === data?.CardId)

      if (card) {
        card.runProcessingAnimation = true

        console.log('llm_search_started update card', card)
        Object.assign(card, card)
      }
    }

    if (data?.Event === 'replace_card_processing_completed') {
      if (route.name === 'InventoryDetails') {
        inventoryStore.updatedInventoryCard(data.CardId, true)
      } else {  
        cardStore.fetchUpdatedCard(data.CardId)
      }

      setTimeout(() => {
        batchStore.replaceImageLoader = false
        globalStore.pusherLoading = false
      }, 0)
    }

    if (
      (data?.Event === 'remove_banner_completed' || data?.Event === 'add_banner_completed') &&
      data.LastCard
    ) {
      cardStore.fetchSelectedCards(null, false)
    }

    if (data?.Event === 'swap_processing_completed') {
      if (route.name === 'InventoryDetails') {
        inventoryStore.updatedInventoryCard(data.CardId, true)
        globalStore.pusherLoading = false
      } else {  
        cardStore.fetchUpdatedCard(data.CardId)
      }

      if (data.LastCard) {
        cardStore.updateSwappedToast(true, 'Image swap complete!')
      }
    }

    if (data?.Event === 'card_reprocessing_completed' && data.LastCard) {
      cardStore.fetchSelectedCards(null, false)
    }

    if (data?.Event === 'scan_counts_updated') {
      configStore.config = {
        ...configStore.config,
        availableScans: data.AvailableScans,
        totalScans: data.TotalScans
      }
    }

    if (data?.Event === 'card_uploaded_to_cloud_storage' && data?.MobileUpload) {
      const filesNames = data?.UploadFilenames && JSON.parse(data?.UploadFilenames)

      // Pusher guarantees at-least-once, not exactly-once: a redelivered event
      // (reconnect, brief drop) used to append a second tile for the same card
      // AND double-count it. The CardId is the identity — ignore a repeat.
      if (batchStore.uploads.some(upload => upload.cardId === data.CardId)) return

      batchStore.uploads = [
        ...batchStore.uploads,
        {
          front: { name: filesNames.frontFilename || '' },
          back: { name: filesNames.backFilename || '' },
          thumbnail: null,
          thumbnailFront: data.Images.FrontSmImage || null,
          thumbnailBack: data.Images.BackSmImage || null,
          progress: 0,
          pending: false,
          uploaded: true,
          cardId: data.CardId
        }
      ]
      batchStore.countOfUploadedImagesFromMobile =
        batchStore.countOfUploadedImagesFromMobile + 1
    }
  }

  const handleImportCompleted = (data, event, typeOfEvent) => {
    console.log('import completed ---', typeOfEvent, data)
    const cardStore = useCardStore()

    if (typeOfEvent !== 'collx_listing_completed') {
      connectionStore.showExportToast = true
      if (typeOfEvent === 'ebay_import_failed') {
        globalStore.errors = true
      }

      const pusherInfo = {
        ...PUSHER_EVENTS_TO_CHANNELS[typeOfEvent].channel,
        label: 'Listings published',
        message: `items have been listed on ${PUSHER_EVENTS_TO_CHANNELS[typeOfEvent].name}.`,
        key: PUSHER_EVENTS_TO_CHANNELS[typeOfEvent].channel?.key === 'ebay' ? 'ebay-direct' : PUSHER_EVENTS_TO_CHANNELS[typeOfEvent].channel?.key,
        countOfCards: data.successful_cards_data?.length,
        ...data
      }
      connectionStore.exportedChannelInfo = pusherInfo
      const pusherKey = pusherInfo.key
      const existingIdx = connectionStore.exportedChannelInfoList.findIndex(i => i.key === pusherKey)
      if (existingIdx >= 0) connectionStore.exportedChannelInfoList[existingIdx] = pusherInfo
      else connectionStore.exportedChannelInfoList.push(pusherInfo)

      let parsedErrors = []
      
      try {
        if (data.failed_cards_data) {
          parsedErrors = JSON.parse(data.failed_cards_data)
        }
      } catch {
        parsedErrors = []
      }
      
      connectionStore.exportedErrorsInfo = {
        errors: [...parsedErrors],
        channel: PUSHER_EVENTS_TO_CHANNELS[typeOfEvent].channel,
        name: PUSHER_EVENTS_TO_CHANNELS[typeOfEvent].name
      }
    }
    

    if (data.successful_cards_data && data.successful_cards_data.length && typeOfEvent !== 'collx_import_completed') {
      // Update cards in cardStore based on successful_cards_data
      cardStore.updateCardsFromSuccessfulData(data.successful_cards_data)
    }
    
    // if (route.params.batchId) {
    //   cardStore.fetchSelectedCards(null, false)
    // }
  }

  const handleOtpLoginCompleted = () => {
    if (!isMobile()) {
      // Use the batchId captured at QR generation time — route.params.batchId is unreliable
      // here because the user may have navigated away from the batch page before this event fires.
      const batchId = batchStore.otpBatchId || route.params.batchId
      if (!batchId) return
      router.push(`/batches/${batchId}/add/upload`)
      userStore.otpLoginDone = true
    }
  }

  const handleSelectedImageSide = (data) => {
    if (!isMobile()) {
      router.replace(`${route.path}?sides=${data.side}`)
    }
  }

  const handleOtpLoginEnded = () => {
    if (isMobile()) {
      router.push(`/mobile-scan/session-ended`)
    }
  }

  // `collx_auth_failed` is broadcast on the user's own channel by ANY server-side
  // CollX job that hits an auth error, including jobs the user never started.
  // It reached dealers who have NEVER connected CollX and have no CollX
  // listings — reported while deleting duplicates, which yanked them out of the
  // grid onto the CollX sign-in screen mid-delete (this `router.push` is the
  // only unprompted route to that screen; every other one is a click on a
  // Connections surface). Why the backend fires it for these accounts is a
  // backend question, and not one the client should try to answer.
  //
  // Only an account that HAS a CollX connection can have one that expired, so
  // for everyone else this event is noise and must not move them. Same connected
  // test the rest of the app uses (credential flag, legacy user field as
  // fallback); an unloaded credential list reads as "not connected", which fails
  // in the safe direction — no navigation. Returning here also skips
  // `collxAuthFailedFunc`, which would otherwise PATCH `collx_enabled: false`
  // onto a user who never had CollX at all.
  const handleCollxAuthFailed = () => {
    const isCollxConnected = Boolean(
      userStore.credentialByType('collx')?.enabled || userStore.user?.collx_enabled
    )

    if (!isCollxConnected) return

    connectionStore.collxAuthFailedFunc()

    router.push(`/connections/collx/account-connection`)
  }

  watch(() => userStore.loggedIn, () => {
    if (userStore.loggedIn) {
      initializePusher()
    }
  })

  onUnmounted(() => {
    if (channel) {
      channel.unbind_all()
      channel.unsubscribe()
    }
    if (pusher) {
      pusher.disconnect()
    }
  })
}
